import { CheckboxButtonGroup } from "common/components/form/CheckboxButtonGroup";
import React, { useEffect, useState } from "react";
import { MUILoadingButton } from "../../components/buttons/MUILoadingButton";
import { showToastError } from "common/toast/toast";
import { LinkBox } from "../../components/links/LinkBox";
import { getPropertyValueString } from "../../helpers/property.helpers";
import { AppraiserProperty } from "../../types/appraiser.types";
import { useTranslation } from "react-i18next";
import { usePenCertificate } from "./hooks/usePenCertificate";

interface PropertyShareSettingsProps {
  property?: AppraiserProperty;
  onChange?: (permissions: PropertyPermission[]) => void;
  initialPermissions?: PropertyPermission[];
  purchasedPermissions?: PropertyPermission[];
  disablePermissions?: boolean;
  children?: React.ReactNode;
}

export enum PropertyPermission {
  Edit = "edit",
  ViewAppraiser = "viewAppraiser",
  ViewGallery = "viewGallery",
  ViewAppraiserComments = "viewAppraiserComments",
  ViewProperty = "viewInfo",
  ViewValue = "viewValue",
  ViewComparables = "viewComparables",
  ViewCompositeAssets = "viewCompositeAssets",
  ViewCalculations = "viewCalculations",
  ViewDocuments = "viewDocuments",
  ViewDistances = "viewDistances",
  ViewMarketInsights = "viewMarketInsights",
  ViewMapsAndClimateRisks = "viewMapsAndClimateRisks",
  ViewPenCertificate = "viewPenCertificate",
  ViewHazardousObjects = "viewHazardousObjects",
  ViewSimilarPropertiesInsights = "viewSimilarPropertiesInsights",
  Legal = "legal",
  ConfidenceMeasure = "confidenceMeasure",
}

/** Shareable, but the appraiser has to tick them on themselves. */
const defaultUncheckedPermissions: PropertyPermission[] = [
  PropertyPermission.ViewPenCertificate,
  PropertyPermission.ViewHazardousObjects,
];

export const basicPermissions = [
  PropertyPermission.ViewAppraiser,
  PropertyPermission.ViewProperty,
  PropertyPermission.ViewValue,
  PropertyPermission.Legal,
  PropertyPermission.ViewDocuments,
  PropertyPermission.ViewGallery,
  PropertyPermission.ViewAppraiserComments,
];

export const PropertyShareSettings: React.FC<PropertyShareSettingsProps> = ({
  property,
  initialPermissions,
  purchasedPermissions,
  disablePermissions,
  onChange,
  children,
}) => {
  const { t } = useTranslation();
  const { hasPenCertificate } = usePenCertificate(property?.uniqueNr);

  const selectValues = [
    {
      label: t("propertyShare.permissions.appraiserInfo"),
      value: PropertyPermission.ViewAppraiser,
    },
    {
      label: t("propertyShare.permissions.aboutPriceEstimation"),
      value: PropertyPermission.Legal,
    },
    {
      label: getPropertyValueString(property),
      value: PropertyPermission.ViewValue,
    },
    {
      label: t("propertyShare.permissions.propertyInfo"),
      value: PropertyPermission.ViewProperty,
    },
    {
      label: t("propertyShare.permissions.gallery"),
      value: PropertyPermission.ViewGallery,
    },
    {
      label: t("propertyShare.permissions.calculationsJustification"),
      value: PropertyPermission.ViewCalculations,
    },
    {
      label: t("propertyShare.permissions.documents"),
      value: PropertyPermission.ViewDocuments,
    },
    {
      label: t("propertyShare.permissions.appraiserComment"),
      value: PropertyPermission.ViewAppraiserComments,
    },
    {
      label: t("propertyShare.permissions.distances"),
      value: PropertyPermission.ViewDistances,
    },
    {
      label: t("propertyShare.permissions.mapsAndClimateRisks"),
      value: PropertyPermission.ViewMapsAndClimateRisks,
    },
    {
      label: t("propertyShare.permissions.hazardousObjects"),
      value: PropertyPermission.ViewHazardousObjects,
    },
    ...(hasPenCertificate
      ? [
          {
            label: t("propertyShare.permissions.penCertificate"),
            value: PropertyPermission.ViewPenCertificate,
          },
        ]
      : []),
    {
      label: t("propertyShare.permissions.marketOverview"),
      value: PropertyPermission.ViewMarketInsights,
    },
    {
      label: t("propertyShare.permissions.similarProperties"),
      value: PropertyPermission.ViewSimilarPropertiesInsights,
    },
    {
      label: t("propertyShare.permissions.confidence"),
      value: PropertyPermission.ConfidenceMeasure,
    },
  ];

  const settings = selectValues.map((v) => ({
    ...v,
    label: purchasedPermissions?.includes(v.value)
      ? `${v.label} (${t("propertyShare.permissions.purchased")})`
      : v.label,
  }));

  const basicSettings = settings.filter((s) =>
    basicPermissions.includes(s.value)
  );
  const otherSettings = settings.filter(
    (s) => !basicPermissions.includes(s.value)
  );

  const disabledPermissions = [
    ...(purchasedPermissions?.filter((p) => !basicPermissions.includes(p)) ??
      []),
    ...basicPermissions,
  ];

  const allSelectablePermissions = settings
    .map((s) => s.value)
    .filter(
      (value) =>
        !disabledPermissions.includes(value) &&
        !defaultUncheckedPermissions.includes(value)
    );

  const mergedPermissions = Array.from(
    new Set([...(initialPermissions ?? []), ...allSelectablePermissions])
  );

  const [selectedPermissions, setSelectedPermissions] =
    useState<PropertyPermission[]>(mergedPermissions);

  useEffect(() => {
    onChange?.(selectedPermissions);
  }, []);

  return (
    <div style={{ width: "500px", padding: "20px" }}>
      <CheckboxButtonGroup
        values={[
          {
            label: t("propertyShare.permissions.selectAll"),
            value: undefined,
            children: [
              {
                label: t("propertyShare.permissions.basicInfo"),
                value: undefined,
                children: basicSettings,
              },
              ...otherSettings,
            ],
          },
        ]}
        disabledValues={disablePermissions ? disabledPermissions : []}
        selectedValues={selectedPermissions}
        onSelect={(perms) => {
          const newPermissions = [...selectedPermissions, ...perms];
          setSelectedPermissions(newPermissions);
          onChange?.(newPermissions);
        }}
        onUnselect={(perms) => {
          const newPermissions = selectedPermissions.filter(
            (p) => !perms.includes(p)
          );
          setSelectedPermissions(newPermissions);
          onChange?.(newPermissions);
        }}
        backgroundColor="white"
      />
      {children}
    </div>
  );
};
